import { useEffect, useState } from "react";
import { GetProfile } from "../queries";
import { useQuery, useMutation } from "@apollo/react-hooks";
import useInputState from "../../../hooks/useInputState";
import { Intent } from "@blueprintjs/core";
import { updateAccountDomain } from "./mutation";
import DefaultToaster from "../../../atoms/DefaultToaster";
import useTutorialsState from "../../../hooks/useTutorialsState";
import useTagAccount from "../../../hooks/useTagAccount";

export const useChangeCustomDomain = () => {
  const { data, loading } = useQuery(GetProfile);
  const [videoDomainUrl, setVideoDomainUrl] = useInputState("");
  const [brandDomainUrl, setBrandDomainUrl] = useInputState("");
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState();
  const account = data && data.accounts && data.accounts[0];
  const { getHasCompletedTutorial, completeTutorial } = useTutorialsState();
  const { tagAccount } = useTagAccount();

  useEffect(() => {
    account && setVideoDomainUrl(account.domainUrl);
    account && setBrandDomainUrl(account.brandDomainUrl);
  }, [account]);

  const [saveAccountDomain] = useMutation(updateAccountDomain, {
    refetchQueries: [{ query: GetProfile }]
  });

  const saveChanges = async () => {
    setIsSaving(true);
    setError(undefined);

    try {
      const {
        data: { updateAccountDomain: updateStatus }
      } = await saveAccountDomain({
        variables: {
          domainUrl: videoDomainUrl,
          brandDomainUrl: brandDomainUrl
        }
      });

      setIsSaving(false);

      if (updateStatus === "Updated") {
        const hasAddedCustomDomain = getHasCompletedTutorial(
          "Add Custom Domain"
        );

        if (!hasAddedCustomDomain) {
          await completeTutorial({
            variables: { name: "Add Custom Domain", version: 1 }
          });

          await tagAccount("Custom Domain Added");
        }

        DefaultToaster.show({
          message: "Your custom domain has been saved! Please allow up to 72 hours after applying your CNAME changes for your domain to become active.",
          icon: "tick",
          intent: Intent.SUCCESS,
          timeout: 0
        });
      } else {
        setError({
          message: "An error occurred while updating the record."
        });
      }
    } catch (err) {
      setError({
        message: "An error occurred while updating the record."
      });
    }
  };

  const cancelChanges = () => {
    setError(undefined);
    setVideoDomainUrl(account.domainUrl);
    setBrandDomainUrl(account.brandDomainUrl);
    DefaultToaster.show({
      message: "Account domain url restored!",
      icon: "tick",
      intent: Intent.SUCCESS
    });
  };

  return {
    videoDomainUrl,
    setVideoDomainUrl,
    brandDomainUrl,
    setBrandDomainUrl,
    isSaving,
    error,
    account,
    saveChanges,
    cancelChanges,
    loading: loading || !data,
    isSaveEnabled: !isSaving
  };
};
